/* eslint-disable eqeqeq */
import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import { handleTabToNext } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import {
  AccSubtab,
  accList,
  APSelect,
  ASelect,
} from '../accessoryFieldKit';
import MountingSection from '../MountingSection';
import AccessoryMountingPopup, {
  CasingMountSpecialParameters,
} from '../AccessoryMountingPopup';
import { CasingMountRow } from './LouverForm';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 2 (dev-sync) B4 — VFD form as dev's READ-ONLY VIEWER
 * (accessories/vfd/{form.js, form.tmpl} on dev — diff vs 3de2283):
 *   - the form-heading smartlist/reset block is REMOVED (empty heading;
 *     form.js adds noFetchCount — mirrored in accessoryFormConfigs.ts);
 *   - Properties + Mounting sublists carry `non-clickable` at the <ul> level
 *     (pointer-events:none — _general.scss:30); controls additionally get
 *     `disabled` here so keyboard edits can't slip a commit through;
 *   - the "Fan Controlled" row is DELETED; "VFD to Motor | Qty" splits into a
 *     "VFD to Motor" select row + a readonly "Quantity" row (tmpl :69-86);
 *   - Mounting gains "Bank Width x Height" + "Width x Height" readonly rows
 *     (tmpl :173-200); the In-Enclosure row hides unless housed_in_enclosure
 *     and its checkbox goes visibility:hidden (tmpl :159-172);
 *   - the accessory-list delete icon hides when fan_motor_id is set
 *     (form.js:135-140 _toggleDeleteIcon) — lives in TabAccessories.tsx (the
 *     icon renders there, outside this form).
 * The VFD is EDITED from the fan selector's VFD input tab (FanVfdTab); this
 * form only displays the persisted state. Dev's pricing-sublist non-clickable
 * change is N/A — the React port never carried the pricing sublist.
 * Deviation (pre-existing): the postRenderCallback description-overflow
 * tooltip (_updateDescriptionSelectionTooltip) is not ported.
 */
const config = ACCESSORY_FORM_CONFIGS.vfd;

const VOLTAGES_URL = '/api/v1.0/master/v1.0.0/ahu/voltages/';

/** the Handlebars get_one_decimal helper (ahu/libs/handlebars/helpers.js:
 * 154-157): first truthy non-object arg → Number → toFixed(2); falsy/NaN → ''. */
const getOneDecimal = (...args: unknown[]): string => {
  const found = args.find((el) => el && typeof el !== 'object');
  const n = Number(found);
  return n ? n.toFixed(2) : '';
};

const VfdForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting'],
  });
  const { data: voltages } = useGetMasterListByUrlQuery(VOLTAGES_URL);
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const params = (name: string) => api.catalogueFields.master_vfd_id?.paramValue(name);
  const housed = !!model.get('housed_in_enclosure');
  const inEnclosure = !!model.get('enclosure_id');
  const qty = params('qty') ? String(params('qty')) : '1';
  const nemaName = (() => {
    const list = accList(api.cmsAccessory, 'nema_encl_type');
    const found = list.find((o) => o.id == params('nema_encl_type_id'));
    return found ? String(found.name ?? '') : '';
  })();

  return (
    <>
      {/* dev removed the smartlist/reset heading block — empty heading. */}
      <div className="form-heading" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist vfd">
          <AccSubtab
            api={api}
            type="vfd"
            cid={cid}
            name="properties"
            // dev form.tmpl:8 — the whole Properties sublist is non-clickable.
            sublistClass="max-height-auto non-clickable"
          >
            <APSelect api={api} target="master_vfd_id" name="mfg_id" label="Manufacturer" required cmsPath="manufacturer" disabled />
            <APSelect api={api} target="master_vfd_id" name="series_id" label="Series" required cmsPath="series" labelKey="series" disabled />
            <APSelect api={api} target="master_vfd_id" name="model" label="Model" required cmsPath="model" labelKey="model" valueKey="model" disabled />
            <APSelect api={api} target="master_vfd_id" name="description_id" label="Description" cmsPath="description" hideNone={false} disabled />
            <APSelect api={api} target="master_vfd_id" name="voltage_id" label="Voltage" options={voltages ?? []} disabled />
            {/* "Fan Controlled" row DELETED on dev. "VFD to Motor | Qty"
                split into two rows (dev form.tmpl:69-86). */}
            <ASelect
              api={api}
              name="configuration_id"
              label="VFD to Motor "
              cmsPath="configuration"
              hideNone={false}
              disabled
            />
            <li>
              <div className="single-line">
                <input
                  type="text"
                  value={qty}
                  readOnly
                  tabIndex={-1}
                  className="vfd-output"
                  onChange={() => undefined}
                />
                <span className="label">Quantity</span>
              </div>
            </li>
            {/* NEMA Type — readonly params echo input (tmpl :110). Dev-sync I
                ("VFD: Remove Nema Type for OJ series", form.tmpl:87/:103 on
                merged dev): the row renders only when the VFD has a
                nema_encl_type_id — OJ VFDs carry none, so it disappears.
                Handlebars {{#if}} semantics: 0 truthy, null/''/undefined
                falsy. */}
            {(() => {
              const nemaId = params('nema_encl_type_id');
              return nemaId != null && nemaId !== '' && nemaId !== false;
            })() && (
              <li>
                <div className="single-line">
                  <input
                    type="text"
                    autoComplete="off"
                    className="vfd_params_field outpt-text"
                    name="nema_encl_type_id"
                    data-step="0.01"
                    value={nemaName}
                    readOnly
                    tabIndex={-1}
                    onChange={() => undefined}
                  />
                  <span className="label">NEMA Type</span>
                </div>
              </li>
            )}
            {/* Power (tmpl :115-121). */}
            <li>
              <div className="multiply-total multiply vfd-power">
                <span className="unit">hp</span>
                <input
                  type="text"
                  name="power_id"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  value={String(params('power') ?? '')}
                  readOnly
                  tabIndex={-1}
                  className=""
                  onChange={() => undefined}
                />
                <span className="label">Power </span>
              </div>
            </li>
            {/* Rating: fla select × qty × fla echo (tmpl :122-141). */}
            <li>
              <div className="multiply-total vfd-power">
                <span className="unit">Amp</span>
                <span className="selection">{String(params('fla') ?? '')}</span>
                <select
                  name="fla"
                  className="vfd_params_field small-input"
                  required
                  disabled
                  value={params('fla') != null ? String(params('fla')) : 'None'}
                  onChange={(e) => api.commitParamsField('master_vfd_id', 'fla', e.target.value)}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  {accList(api.cmsAccessory, 'fla').map((o) => (
                    <option key={String(o.fla)} value={String(o.fla)}>
                      {String(o.fla ?? '')}
                    </option>
                  ))}
                </select>
                <span className="multiply-sign vfd-output">X</span>
                <input
                  type="text"
                  value={qty}
                  readOnly
                  tabIndex={-1}
                  className=" vfd-output output-xsmall"
                  onChange={() => undefined}
                />
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  className="vfd_params_field small-input vfd-output"
                  name="fla"
                  data-step="0.01"
                  value={String(params('fla') ?? '')}
                  readOnly
                  tabIndex={-1}
                  onChange={() => undefined}
                />
                <span className="label">Rating</span>
              </div>
            </li>
            <ASelect api={api} name="wiring_id" label="Wiring" cmsPath="wiring" hideNone={false} disabled />
            <ASelect api={api} name="supplied_by_id" label="Supplied By" cmsPath="supplied_by" hideNone disabled />
          </AccSubtab>

          {/* dev #1033 (form.tmpl:157 `{{#unless model.skip_3d_model}}`): an
              Annexair in-motor VFD has no 3D model, so the whole Mounting
              sub-tab is omitted from the data bar (the rating dialog's VFD
              tab hides its mounting rows the same way — FanVfdTab). */}
          {!model.get('skip_3d_model') && (
          <MountingSection
            api={api}
            type="vfd"
            cid={cid}
            variant="clone"
            // dev form.tmpl:158 — the Mounting sublist is ALWAYS non-clickable
            // (the leading class; the master-null gate became redundant).
            sublistDisabled
            sublistExtraClass={!housed ? ' active-group ' : undefined}
            locationBtnDisabled={inEnclosure}
            planeLabelRequired={false}
            leadingChildren={
              <>
                {/* In Enclosure W x H — row hidden unless housed; checkbox
                    visibility:hidden (dev form.tmpl:159-172). */}
                <li className={housed ? undefined : 'hide'}>
                  <div className="multiply vfd-power">
                    <label style={{ paddingTop: 0, visibility: 'hidden' }}>
                      <input
                        style={{ visibility: 'hidden' }}
                        type="checkbox"
                        className="housed_in_enclosure model_field"
                        checked={housed}
                        name="housed_in_enclosure"
                        onChange={() => undefined}
                      />
                    </label>
                    <span className="unit">in</span>
                    {/* Enclosure dims contract (dev models_component_fan.py
                      * AHUAccessoryVFD.save(): `width`/`height` are force-set
                      * from the master VFD). Display-only here — the values
                      * are the serializer's computed enc_width/enc_height
                      * (dev form.tmpl :165/:169; DOM names stay width/height
                      * per the tmpl). Edits happen in the fan selector's VFD
                      * input tab. */}
                    <input
                      type="text"
                      name="width"
                      data-formatter_type="decimal"
                      value={String(model.get('enc_width') ?? '')}
                      className={`medium-input model_field housed_in_enclosure_input${
                        !housed ? ' non-clickable' : ''
                      }`}
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="multiply-sign vfd-output">X</span>
                    <input
                      type="text"
                      name="height"
                      data-formatter_type="decimal"
                      value={String(model.get('enc_height') ?? '')}
                      className={`medium-input model_field housed_in_enclosure_input${
                        !housed ? ' non-clickable' : ''
                      }`}
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="label big-label">In Enclosure W x H</span>
                  </div>
                </li>
                {/* NEW dev row (form.tmpl:173-186): Bank Width x Height —
                    get_one_decimal(bank_*, fallback width/height). */}
                <li>
                  <div className="multiply vfd-power">
                    <label style={{ paddingTop: 0, visibility: 'hidden' }}>
                      <input
                        style={{ visibility: 'hidden' }}
                        type="checkbox"
                        className="housed_in_enclosure model_field"
                        checked={housed}
                        name="housed_in_enclosure"
                        onChange={() => undefined}
                      />
                    </label>
                    <span className="unit">in</span>
                    <input
                      type="text"
                      name="bank_wide"
                      data-formatter_type="decimal"
                      value={getOneDecimal(model.get('bank_width'), model.get('width'))}
                      className="medium-input model_field housed_in_enclosure_input"
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="multiply-sign vfd-output">X</span>
                    <input
                      type="text"
                      name="bank_high"
                      data-formatter_type="decimal"
                      value={getOneDecimal(model.get('bank_height'), model.get('height'))}
                      className="medium-input model_field housed_in_enclosure_input"
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="label big-label">Bank Width x Height</span>
                  </div>
                </li>
                {/* NEW dev row (form.tmpl:187-200): Width x Height — raw
                    model.width/height; input names stay bank_wide/bank_high
                    (dev's copy-paste, kept verbatim for parity). */}
                <li>
                  <div className="multiply vfd-power">
                    <label style={{ paddingTop: 0, visibility: 'hidden' }}>
                      <input
                        style={{ visibility: 'hidden' }}
                        type="checkbox"
                        className="housed_in_enclosure model_field"
                        checked={housed}
                        name="housed_in_enclosure"
                        onChange={() => undefined}
                      />
                    </label>
                    <span className="unit">in</span>
                    <input
                      type="text"
                      name="bank_wide"
                      data-formatter_type="decimal"
                      value={String(model.get('width') ?? '')}
                      className="medium-input model_field housed_in_enclosure_input"
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="multiply-sign vfd-output">X</span>
                    <input
                      type="text"
                      name="bank_high"
                      data-formatter_type="decimal"
                      value={String(model.get('height') ?? '')}
                      className="medium-input model_field housed_in_enclosure_input"
                      style={{ width: '115px' }}
                      readOnly
                      tabIndex={-1}
                      onChange={() => undefined}
                    />
                    <span className="label big-label">Width x Height</span>
                  </div>
                </li>
              </>
            }
          >
            {/* inline casing_mount radio row (vfd tmpl :402-414 — 6e2b verifier). */}
            <CasingMountRow api={api} />
          </MountingSection>
          )}
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="vfd"
        typeValue="vfd"
        SpecialParameters={CasingMountSpecialParameters}
      />
    </>
  );
};

export default VfdForm;
